/**
 * World-space hit testing, shared by the phone screen and the web view. The
 * screen converts its touch tolerance (pixels) to feet with the view scale.
 */
import { POLICY, WALL_FT } from './policy';
import { EPS, area, bbox, clamp, dist, inPoly, labelPt, segDist, unit } from './geom';
import { adjacent, mismatches, openGeom, openHidden, sharedMap, type Mismatch } from './shared';
import type { Plan, Pt, Room } from './types';

export type Hit =
  | { type: 'mark'; m: Mismatch; d: number }
  | { type: 'open'; rid: string; oid: number; d: number }
  | { type: 'wall'; rid: string; vid: number; off: number; d: number }
  | { type: 'room'; rid: string };

export interface HitOptions {
  /** Touch tolerance in feet (screen px / scale). */
  tolFt: number;
  /** A wall of the selected room wins a near-tie (prototype bias). */
  selectedRoomId?: string | null;
  selectedBiasFt?: number;
  /** Tolerance for red gap marks, in feet; omit to skip marks. */
  markTolFt?: number;
  /** A placing tool is armed: a door's leaf and arc are not tap targets (B-7356). */
  toolArmed?: boolean;
}

/** B-7360: the centre zone's radius is half the room's half width over this (structural). */
const CENTRE_ZONE_PER_HALF = 4; // policy-literal-ok: geometry of the zone, not a tunable

/** Half the room's smaller extent: an opening's tap reach never goes past it (B-7356). */
function halfWidth(r: Room): number {
  const [x0, y0, x1, y1] = bbox(r.corners);
  return Math.min(x1 - x0, y1 - y0) / 2;
}

/**
 * Nearest visible opening within tolerance (B-7356). An opening is a tap target only
 * on its own span along the wall (a tap whose foot on the wall lies inside the
 * opening) and, a door with no tool armed, on its leaf and its swing arc's line, never
 * the whole swing. The reach is the touch tolerance capped at half the room's smaller
 * extent, so at fit zoom a door does not cover a small room.
 */
export function nearestOpening(
  plan: Plan, wp: Pt, tolFt: number, opts: { toolArmed?: boolean } = {},
): Extract<Hit, { type: 'open' }> | null {
  return openingHit(plan, wp, tolFt, !!opts.toolArmed)?.hit ?? null;
}

type OpenHit = { hit: Extract<Hit, { type: 'open' }>; hosts: Set<string> };

/** `nearestOpening` with the walls it sits on (its own and its partner's) when the tap is on its span. */
function openingHit(plan: Plan, wp: Pt, tolFt: number, toolArmed: boolean): OpenHit | null {
  let best: OpenHit | null = null;
  const M = sharedMap(plan);
  for (const r of plan.rooms) {
    const A = area(r);
    const reach = Math.min(tolFt, halfWidth(r));
    for (const o of r.openings) {
      if (openHidden(plan, r, o, M)) continue;
      const g = openGeom(r, o, M, A);
      if (!g) continue;
      const s = (wp.x - g.E.a.x) * g.u.x + (wp.y - g.E.a.y) * g.u.y;
      let d = Infinity, onSpan = false;
      if (s >= g.s0 && s <= g.s1) {
        onSpan = true;
        d = segDist(wp, g.p0, g.p1);
        if (g.thick) {
          const half = WALL_FT / 2;
          d = Math.min(d, segDist(wp,
            { x: g.p0.x + g.nO.x * half, y: g.p0.y + g.nO.y * half },
            { x: g.p1.x + g.nO.x * half, y: g.p1.y + g.nO.y * half }));
        }
      }
      if (!toolArmed && o.kind === 'door' && g.hinge && g.leaf && g.side) {
        d = Math.min(d, segDist(wp, g.hinge, g.leaf));
        const sd = (wp.x - g.hinge.x) * g.side.x + (wp.y - g.hinge.y) * g.side.y;
        if (sd > 0) d = Math.min(d, Math.abs(dist(wp, g.hinge) - g.w));
      }
      if (d < reach && (!best || d < best.hit.d)) {
        const hosts = new Set<string>();
        if (onSpan) {
          hosts.add(`${r.id}:${o.edgeStart}`);
          const P = adjacent(plan, r, o, M);
          if (P) hosts.add(`${P.r.id}:${P.o.edgeStart}`);
        }
        best = { hit: { type: 'open', rid: r.id, oid: o.id, d }, hosts };
      }
    }
  }
  return best;
}

/** Nearest wall within tolerance, with the offset along it (`skip`: walls, as "rid:vid", left out). */
export function nearestWall(
  plan: Plan, wp: Pt, tolFt: number, selectedRoomId?: string | null, biasFt = 0, skip?: Set<string>,
): Extract<Hit, { type: 'wall' }> | null {
  let best: Extract<Hit, { type: 'wall' }> | null = null;
  for (const r of plan.rooms) {
    const n = r.corners.length;
    for (let i = 0; i < n; i++) {
      const a = r.corners[i], b = r.corners[(i + 1) % n];
      if (dist(a, b) < 1e-6 || skip?.has(`${r.id}:${a.id}`)) continue;
      let d = segDist(wp, a, b);
      if (selectedRoomId && selectedRoomId === r.id) d -= biasFt;
      if (d < tolFt && (!best || d < best.d)) {
        const u = unit(a, b);
        best = { type: 'wall', rid: r.id, vid: a.id, off: clamp((wp.x - a.x) * u.x + (wp.y - a.y) * u.y, 0, dist(a, b)), d };
      }
    }
  }
  return best;
}

/** Topmost room containing the point. */
export function roomAt(plan: Plan, wp: Pt): string | null {
  for (let i = plan.rooms.length - 1; i >= 0; i--) if (inPoly(plan.rooms[i], wp)) return plan.rooms[i].id;
  return null;
}

/** Prototype priority: gap mark, opening, wall, room. */
export function hitTest(plan: Plan, wp: Pt, opts: HitOptions): Hit | null {
  if (opts.markTolFt != null) {
    let bestMark: Extract<Hit, { type: 'mark' }> | null = null;
    for (const m of mismatches(plan)) {
      const d = dist(m.pos, wp);
      if (d < opts.markTolFt && (!bestMark || d < bestMark.d)) bestMark = { type: 'mark', m, d };
    }
    if (bestMark) return bestMark;
  }
  // B-7360 (FIELD-4 3.10): a tap near a room's centre selects the room, whatever door's
  // swing reaches it (a 2'6" x 3' bath at fit zoom): within an eighth of the room's smaller
  // extent of its label point. Walls and door spans are always farther out than that.
  const cr = roomAt(plan, wp);
  const centreRoom = cr ? plan.rooms.find((q) => q.id === cr) : undefined;
  if (centreRoom && dist(wp, labelPt(centreRoom)) <= halfWidth(centreRoom) / CENTRE_ZONE_PER_HALF) return { type: 'room', rid: centreRoom.id };
  const o = openingHit(plan, wp, opts.tolFt, !!opts.toolArmed);
  // B-7356: an opening wins unless another wall is nearer. On its span, the wall it sits
  // on (either side of a shared wall, J14) is the opening there and does not compete.
  const plain = o ? nearestWall(plan, wp, opts.tolFt, null, 0, o.hosts) : null;
  if (o && (!plain || o.hit.d <= plain.d + EPS)) return o.hit;
  const w = nearestWall(plan, wp, opts.tolFt, opts.selectedRoomId, opts.selectedBiasFt ?? 0);
  if (w) return w;
  const rid = roomAt(plan, wp);
  return rid ? { type: 'room', rid } : null;
}

/**
 * FIELD-2 E17: a long press opens room properties only inside a room and more
 * than `long_press_room_clear_ft` from every one of its walls (nearer a wall it
 * stays the break gesture). Returns the room id, or null.
 */
export function roomPropsPressAt(plan: Plan, wp: Pt): string | null {
  const rid = roomAt(plan, wp);
  const r = rid ? plan.rooms.find((q) => q.id === rid) : undefined;
  if (!r) return null;
  const n = r.corners.length;
  for (let i = 0; i < n; i++) {
    if (segDist(wp, r.corners[i], r.corners[(i + 1) % n]) <= POLICY.long_press_room_clear_ft) return null;
  }
  return r.id;
}
